import { Gamepad2, Play, Eye, Heart, Users, Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Game } from '../../types/api'
import GameOptionsDropdown from '../common/GameOptionsDropdown'
import { TeamTag } from '../common/TeamTag'

interface CompletedGameCardProps {
  project: Game
  index: number
  onArchive: (project: Game) => void
  onEdit: (project: Game) => void
  onDuplicate: (project: Game) => Promise<void>
  onTogglePublish: (project: Game) => Promise<void>
  onToggleVisibility: (project: Game) => Promise<void>
  onTransfer?: (project: Game) => void
  isActionInProgress: boolean
  formatTimeAgo: (dateString: string) => string
  getTagStyle: (tag: string, index: number) => { icon: any, bgColor: string, textColor: string, label: string }
}

export const CompletedGameCard = ({
  project,
  index,
  onArchive,
  onEdit,
  onDuplicate,
  onTogglePublish,
  onToggleVisibility,
  onTransfer,
  isActionInProgress,
  formatTimeAgo,
  getTagStyle
}: CompletedGameCardProps) => {
  const navigate = useNavigate()
  
  const projectId = project.id
  const isPublished = project.status === 'active'
  const isPrivate = project.content?.visibility === 'private'
  
  // Format number with K/M suffix
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }
  
  // Real stats from backend (use 0 as fallback)
  // Plays: total_plays is the primary stat, falls back to total_downloads
  const plays = formatNumber(project.total_plays || project.total_downloads || 0)
  // Views: total_avg_viewers from streaming stats
  const views = formatNumber(project.total_avg_viewers || 0)
  // Favorites: total_favorites is the primary stat, falls back to total_likes
  const faves = formatNumber(project.total_favorites || project.total_likes || 0)

  return (
    <div
      className="group relative bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={() => navigate(`/projects/${projectId}`)}
    >
      {/* Cover Image */}
      <div className="relative aspect-[3/1] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 overflow-hidden rounded-t-xl">
        {project.content?.cover_image_url || project.content?.icon_url ? (
          <img 
            src={project.content?.cover_image_url || project.content?.icon_url} 
            alt={`${project.name} cover`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <Gamepad2 className="w-16 h-16 text-gray-400" />
          </div>
        )}
        
        {/* Stats in bottom right corner */}
        <div className="absolute bottom-3 right-3 flex items-center gap-3 bg-black/30 backdrop-blur-sm rounded-lg px-3 py-1.5">
          <div className="flex items-center gap-1">
            <Play className="w-3.5 h-3.5 text-white fill-white" />
            <span className="text-sm font-bold text-white">{plays}</span>
          </div>
          <div className="flex items-center gap-1">
            <Eye className="w-3.5 h-3.5 text-white" />
            <span className="text-sm font-bold text-white">{views}</span>
          </div>
          <div className="flex items-center gap-1">
            <Heart className="w-3.5 h-3.5 text-white fill-white" />
            <span className="text-sm font-bold text-white">{faves}</span>
          </div>
        </div>
        
        {/* Status Badges overlaid on cover */}
        <div className="absolute top-3 left-3 flex gap-2">
          {/* Playing Badge - DISABLED */}
          {/* {isPublished && (
            <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center gap-1">
              <Users className="w-3 h-3" />
              {activePlayers} Playing
            </div>
          )} */}
          
          {/* Private Badge */}
          {isPrivate && (
            <div className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-500 text-white flex items-center gap-1 shadow-lg">
              <Lock className="w-3 h-3" />
              Private
            </div>
          )}
        </div>
      </div>
      
      {/* Three-dot Menu */}
      <div className="absolute top-3 right-3 z-20">
        <GameOptionsDropdown
          project={project}
          onPublish={project.status === 'inactive' ? onTogglePublish : undefined}
          onUnpublish={project.status === 'active' ? onTogglePublish : undefined}
          onMakePrivate={project.content?.visibility === 'public' ? onToggleVisibility : undefined}
          onMakePublic={project.content?.visibility === 'private' ? onToggleVisibility : undefined}
          onArchive={onArchive}
          onDuplicate={onDuplicate}
          onTransfer={onTransfer}
          isActionInProgress={isActionInProgress}
          stopPropagation={true}
          size="sm"
          buttonClassName="p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-lg"
        />
      </div>

      {/* Card Content */}
      <div className="p-6">
        {/* Icon, Title and Tags Row */}
        <div className="flex items-start gap-4 mb-4">
          {/* Game Icon */}
          <div className="w-20 h-20 bg-white dark:bg-dark-surface rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-md overflow-hidden flex-shrink-0">
            {project.content?.icon_url ? (
              <img 
                src={project.content.icon_url} 
                alt={`${project.name} icon`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  const parent = e.currentTarget.parentElement
                  if (parent) {
                    parent.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"><svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="12" y2="12"/><line x1="8" x2="8" y1="10" y2="14"/><line x1="15" x2="15.01" y1="13" y2="13"/><line x1="18" x2="18.01" y1="11" y2="11"/><rect width="20" height="16" x="2" y="4" rx="2"/></svg></div>'
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <Gamepad2 className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>

          {/* Title and Tags */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 dark:text-dark-text mb-2 truncate">
              {project.name}
            </h3>
            
            {/* Category Tags */}
            <div className="flex gap-2 flex-wrap mb-2">
              {(project.content?.tags?.slice(0, 3) || [project.content?.category || 'Game']).map((tag, tagIndex) => {
                const tagStyle = getTagStyle(tag, tagIndex)
                const TagIcon = tagStyle.icon
                return (
                  <span 
                    key={`tag-${tagIndex}`}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 ${tagStyle.bgColor} ${tagStyle.textColor}`}
                  >
                    <TagIcon className="w-3.5 h-3.5" />
                    {tagStyle.label}
                  </span>
                )
              })}
            </div>
            
            {/* Team Tag */}
            <TeamTag team={project.team} size="sm" />
          </div>
        </div>
        
        {/* Version and Updated */}
        <div className="text-xs text-gray-500 dark:text-gray-400 text-left">
          <span>Launched </span>
          <span className="font-semibold text-gray-700 dark:text-gray-300">
            Version {project.current_bundle_version || project.stats?.versionCount || '1'}
          </span>
          <span> · {formatTimeAgo(project.updated_at || project.created_at)}</span>
        </div>
      </div>
    </div>
  )
}

